<template>
  <div v-if="comment">
    <!-- Parent -->
    <div v-if="!comment.parent">
      <div
        class="comment__block-wrapper"
        :class="{ scheduled: comment.isScheduled }"
      >
        <div
          class="comment__block d-flex pl-0 pr-1 mt-3"
          :class="{
            'comment__block--opened': hasReplies && !repliesHidden,
          }"
        >
          <div class="w-100">
            <div class="d-flex comment__header ml-3">
              <div>
                <HMMemberIcon
                  v-if="comment.user"
                  :id="setCommentUserId(comment)"
                  :icon="comment.user.image"
                  :fullName="`${comment.user.first_name} ${comment.user.last_name}`"
                  :title="`${comment.user.first_name} ${comment.user.last_name}`"
                  :color="studio.primary_color"
                  :fontSize="18"
                  tabindex="0"
                  size="48px"
                  class="pointer mr-2"
                />
              </div>
              <div>
                <div
                  class="h5 mb-0 d-flex flew-row align-items-center font-weight-normal"
                >
                  <span class="author-name"
                    >{{ comment.user.first_name }}
                    {{ comment.user.last_name }}</span
                  >
                  <HMTag
                    class="author-role ml-1 smaller small-radius d-flex align-items-center"
                    :class="comment.additional_data.user_role"
                    v-if="comment.additional_data.user_role"
                  >
                    {{
                      $t(
                        `COMMUNITY.TAGS.${comment.additional_data.user_role.toUpperCase()}`
                      )
                    }}
                  </HMTag>
                </div>
                <span class="comment__label">{{
                  dateFromNow(comment.created)
                }}</span>
              </div>
              <div class="popup-menu">
                <HMIconButton
                  :icon="['far', 'ellipsis-v']"
                  :id="`popover-${comment.id}`"
                />
                <HMPopover
                  :target="`popover-${comment.id}`"
                  placement="bottomleft"
                  offset="-50"
                  :custom-class="'community-popup'"
                >
                  <div v-if="canModerateComment(comment)">
                    <HMPopoverMenuItem
                      @click="hideComment(comment)"
                      v-if="!comment.hidden && !comment.isScheduled"
                    >
                      {{ $t('COMMUNITY.LINK.HIDE_COMMENT') }}
                    </HMPopoverMenuItem>
                    <HMPopoverMenuItem
                      @click="showComment(comment)"
                      v-if="comment.hidden"
                    >
                      {{ $t('COMMUNITY.LINK.SHOW_COMMENT') }}
                    </HMPopoverMenuItem>
                    <HMPopoverMenuItem
                      @click="pinComment(comment)"
                      v-if="interactAsStudio && !comment.is_pinned"
                    >
                      {{ $t('COMMUNITY.LINK.PIN_COMMENT') }}
                    </HMPopoverMenuItem>
                    <HMPopoverMenuItem
                      @click="unPinComment(comment)"
                      v-if="interactAsStudio && comment.is_pinned"
                    >
                      {{ $t('COMMUNITY.LINK.UNPIN_COMMENT') }}
                    </HMPopoverMenuItem>
                    <HMPopoverMenuItem
                      v-if="canEditComment(comment)"
                      @click="openEditModel(comment)"
                    >
                      {{ $t('COMMUNITY.LINK.EDIT_COMMENT') }}
                    </HMPopoverMenuItem>
                    <HMPopoverMenuItem
                      v-confirm="{
                        title: $t('COMMUNITY.LINK.DELETE'),
                        text: $t('COMMUNITY.TEXT.DELETE_COMMENT_DESCRIPTION'),
                        confirmButton: $t('COMMUNITY.LINK.DELETE'),
                        confirm: () => deleteComment(comment),
                      }"
                    >
                      {{ $t('COMMUNITY.LINK.DELETE_COMMENT') }}
                    </HMPopoverMenuItem>
                  </div>
                  <div v-else>
                    <HMPopoverMenuItem @click="viewProfile(comment)">
                      {{ $t('COMMUNITY.LINK.VIEW_PROFILE') }}
                    </HMPopoverMenuItem>
                    <HMPopoverMenuItem
                      @click="flagComment(comment)"
                      v-if="!comment.flagged && !comment.is_pinned"
                    >
                      {{ $t('COMMUNITY.LINK.FLAG_COMMENT') }}
                    </HMPopoverMenuItem>
                    <HMPopoverMenuItem
                      @click="unFlagComment(comment)"
                      v-if="comment.flagged && !comment.is_pinned"
                    >
                      {{ $t('COMMUNITY.LINK.UNFLAG_COMMENT') }}
                    </HMPopoverMenuItem>
                  </div>
                </HMPopover>
              </div>
            </div>
            <div class="comment__like-reply-block px-3">
              <!-- Popover for the user's comment -->
              <HMInfoPopover
                placement="right"
                size="medium"
                :target="setCommentUserId(comment)"
                desktopTriggers="click blur"
              >
                <CommunityProfilePreview :user="comment.user" />
              </HMInfoPopover>

              <!-- Popover for the comment's mention -->
              <div
                v-html="getEditText(comment)"
                class="mt-3 comment-content"
                :style="{ opacity: commentOpacity(comment) }"
              ></div>
              <div v-if="hasAttachment(comment)">
                <HMAttachmentPreview
                  class="mt-3"
                  :data="getAttachment(comment)"
                  :showRemoveIcon="false"
                />
                <hr />
              </div>
              <div
                v-if="!comment.isScheduled"
                class="d-flex justify-content-between align-items-center mt-2"
              >
                <div>
                  <span v-if="!comment.liked">
                    <i
                      class="comment__like-icon far fa-heart pointer charcoal-on-hover"
                      @click="likeComment(comment)"
                    ></i>
                    <span
                      :class="{
                        'comment__like-number--active pointer':
                          comment.num_likes,
                      }"
                      @click="comment.num_likes && showLikes(comment)"
                    >
                      {{ comment.num_likes }}
                    </span>
                  </span>
                  <span v-else>
                    <i
                      class="comment__like-icon fas fa-heart pointer charcoal-on-hover"
                      @click="unlikeComment(comment)"
                    ></i>
                    <span
                      :class="{
                        'comment__like-number--active pointer':
                          comment.num_likes,
                      }"
                      @click="comment.num_likes && showLikes(comment)"
                    >
                      {{ comment.num_likes }}
                    </span>
                  </span>
                  <i class="mx-2"></i>
                  <span
                    class="charcoal-on-hover pointer"
                    @click="replyOnComment(comment)"
                  >
                    <i class="comment__like-icon far fa-comment"></i>
                    {{ $t('COMMUNITY.LINK.REPLY') }}
                  </span>
                  <i class="mx-2"></i>
                  <span
                    v-if="hasReplies && repliesHidden"
                    class="charcoal-on-hover pointer"
                    @click="repliesHidden = false"
                  >
                    <i class="far fa-chevron-down c-brand-color"></i>
                    {{ $t('COMMUNITY.LINK.SHOW_REPLIES') }}
                  </span>
                  <span
                    v-if="hasReplies && !repliesHidden"
                    class="charcoal-on-hover pointer"
                    @click="repliesHidden = true"
                  >
                    <i class="far fa-chevron-up c-brand-color"></i>
                    {{ $t('COMMUNITY.LINK.HIDE_REPLIES') }}
                  </span>
                </div>
                <div>
                  <button
                    v-if="comment.hashtag"
                    class="hashtag smaller"
                    @click="$emit('onHashtag', comment.hashtag)"
                  >
                    {{ comment.hashtag }}
                  </button>
                  <i
                    v-if="comment.is_pinned"
                    class="far fa-thumbtack c-brand-color ml-3"
                  ></i>
                </div>
              </div>
              <div v-else class="scheduled-comment smaller c-charcoal mt-3">
                <div class="d-flex align-items-center w-100">
                  <span class="calendar-icon mr-2 flex-shrink-0 smaller">
                    <i class="far fa-calendar"></i>
                  </span>
                  <span class="flex-grow-1 c-charcoal">
                    {{ $formatDate(comment.release_on, 'date-time') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <CommunityCompose
          :id="`reply-block__${comment.id}`"
          :numberId="comment.id"
          class="comment__reply-block comment__reply-block--nested"
          :class="{ hidden: !comment.showReply }"
          :user="user"
          :parentId="comment.id"
          @addComment="addComment"
          :buttonTitle="'COMMUNITY.BUTTON.REPLY'"
        />
      </div>
    </div>
    <!-- Child -->
    <div v-else class="d-flex mr-4 flex-column pt-1">
      <div class="d-flex mb-1">
        <HMMemberIcon
          v-if="comment.user"
          :id="setCommentUserId(comment)"
          :icon="comment.user.image"
          :fullName="`${comment.user.first_name} ${comment.user.last_name}`"
          :title="`${comment.user.first_name} ${comment.user.last_name}`"
          :color="studio.primary_color"
          :fontSize="14"
          tabindex="0"
          size="40px"
          class="pointer mr-2"
        />
        <div
          v-if="comment.user"
          class="comment__block-wrapper comment__block-wrapper--small"
        >
          <div class="comment__block comment__block--small mb-2">
            <!-- Popover for the user's comment -->
            <div class="pb-1">
              <div
                class="h5 comment__label mb-0 d-flex flew-row align-items-center"
              >
                {{ comment.user.first_name }} {{ comment.user.last_name }}
                <HMTag
                  class="author-role ml-2 smaller small-radius d-flex align-items-center"
                  :class="comment.additional_data.user_role"
                  v-if="comment.additional_data.user_role"
                >
                  {{
                    $t(
                      `COMMUNITY.TAGS.${comment.additional_data.user_role.toUpperCase()}`
                    )
                  }}
                </HMTag>
              </div>
            </div>
            <HMInfoPopover
              placement="right"
              size="medium"
              :target="setCommentUserId(comment)"
              desktopTriggers="click blur"
            >
              <CommunityProfilePreview :user="comment.user" />
            </HMInfoPopover>

            <!-- Popover for the comment's mention -->
            <div
              v-html="getEditText(comment)"
              class="mt-3 comment-content"
              :style="{ opacity: commentOpacity(comment) }"
            ></div>
            <div
              class="comment__like-block d-flex align-items-center"
              v-if="comment.num_likes > 0"
              @click="showLikes(comment)"
            >
              <div
                class="comment__like-container d-flex align-items-center ml-1"
              >
                <i class="comment__like-icon--comments fas fa-heart"></i>
              </div>
              <span class="comment__label pl-1 pr-1">{{
                comment.num_likes
              }}</span>
            </div>
          </div>
          <div>
            <HMAttachmentPreview
              class="border rounded mb-2"
              v-if="hasAttachment(comment)"
              :data="getAttachment(comment)"
              :showRemoveIcon="false"
            />
          </div>
          <div class="comment__label d-flex align-items-center">
            <span
              v-if="!comment.liked"
              class="charcoal-on-hover ml-2 pointer"
              @click="likeComment(comment)"
            >
              {{ $t('COMMUNITY.LINK.LIKE') }}
            </span>
            <span
              v-else
              class="underline-on-hover ml-2 comment__label--liked pointer"
              @click="unlikeComment(comment)"
            >
              {{ $t('COMMUNITY.LINK.LIKED') }}
            </span>
            <i class="mx-1"></i>
            <span
              class="charcoal-on-hover pointer mr-2"
              @click="replyOnComment(comment)"
            >
              {{ $t('COMMUNITY.LINK.REPLY') }}
            </span>
            <i v-if="canModerateComment(comment)" class="mx-1"></i>
            <div class="pointer">
              <span :icon="['far', 'ellipsis-v']" :id="`popover-${comment.id}`">
                {{ $t('COMMUNITY.TEXT.MORE') }}
              </span>
              <HMPopover
                :target="`popover-${comment.id}`"
                placement="bottomleft"
                offset="-50"
                :custom-class="'community-popup'"
              >
                <div v-if="canModerateComment(comment)">
                  <HMPopoverMenuItem
                    @click="hideComment(comment)"
                    v-if="!comment.hidden"
                  >
                    {{ $t('COMMUNITY.LINK.HIDE_COMMENT') }}
                  </HMPopoverMenuItem>
                  <HMPopoverMenuItem
                    @click="showComment(comment)"
                    v-if="comment.hidden"
                  >
                    {{ $t('COMMUNITY.LINK.SHOW_COMMENT') }}
                  </HMPopoverMenuItem>
                  <HMPopoverMenuItem
                    v-if="canEditComment(comment)"
                    @click="openEditModel(comment)"
                  >
                    {{ $t('COMMUNITY.LINK.EDIT_COMMENT') }}
                  </HMPopoverMenuItem>
                  <HMPopoverMenuItem
                    v-confirm="{
                      title: $t('COMMUNITY.LINK.DELETE'),
                      text: $t('COMMUNITY.TEXT.DELETE_COMMENT_DESCRIPTION'),
                      confirmButton: $t('COMMUNITY.LINK.DELETE'),
                      confirm: () => deleteComment(comment),
                    }"
                  >
                    {{ $t('COMMUNITY.LINK.DELETE_COMMENT') }}
                  </HMPopoverMenuItem>
                </div>
                <div v-else>
                  <HMPopoverMenuItem @click="viewProfile">
                    {{ $t('COMMUNITY.LINK.VIEW_PROFILE') }}
                  </HMPopoverMenuItem>
                  <HMPopoverMenuItem
                    @click="flagComment(comment)"
                    v-if="!comment.flagged && !comment.is_pinned"
                  >
                    {{ $t('COMMUNITY.LINK.FLAG_COMMENT') }}
                  </HMPopoverMenuItem>
                  <HMPopoverMenuItem
                    @click="unFlagComment(comment)"
                    v-if="comment.flagged && !comment.is_pinned"
                  >
                    {{ $t('COMMUNITY.LINK.UNFLAG_COMMENT') }}
                  </HMPopoverMenuItem>
                </div>
              </HMPopover>
            </div>
            <i class="mx-1"></i>
            <span class="comment__label comment__label--medium-grey">
              {{ dateFromNow(comment.created) }}
            </span>
          </div>
        </div>
      </div>
      <CommunityCompose
        :id="`reply-block__${comment.id}`"
        :numberId="comment.id"
        class="comment__reply-block ml-5"
        :class="{ hidden: !comment.showReply }"
        :user="user"
        :parentId="comment.id"
        @addComment="addComment"
        :buttonTitle="'COMMUNITY.BUTTON.REPLY'"
      />
    </div>
    <li>
      <ul>
        <div v-if="!repliesHidden">
          <CommunityCommentNode
            v-for="reply in comment.replies"
            class="pl-3"
            :class="{ 'comment__children pb-1': reply.parent }"
            :comments="[reply]"
            :key="reply.id"
            :user="user"
            @addComment="addComment"
            @editComment="editComment"
            :buttonTitle="'COMMUNITY.BUTTON.REPLY'"
          />
        </div>
      </ul>
    </li>
  </div>
</template>

<script>
import CommunityProfilePreview from '@/web/components/community/CommunityProfilePreview';
import CommunityCompose from '@/web/components/community/CommunityCompose';
import CommentLikesModal from '@/web/components/community/CommentLikesModal';
import CommunityCommentNode from '@/web/components/community/CommunityCommentNode';
import { sanitize } from '@/core/utils/utils';
import { generateHTML } from '@tiptap/html';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Image from '@tiptap/extension-image';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import Code from '@tiptap/extension-code';
import CodeBlock from '@tiptap/extension-code-block';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import Link from '@tiptap/extension-link';
import Italic from '@tiptap/extension-italic';
import Bold from '@tiptap/extension-bold';
import Blockquote from '@tiptap/extension-blockquote';
import moment from 'moment/moment';
import { errorToMessage } from '@/core/utils/errors';
import CommunityCommentEditModal from '@/web/views/admin/community/CommunityCommentEditModal';
import CommunityScheduleModal from '@/web/components/community/CommunityScheduleModal';
import { mapGetters } from 'vuex';
import CommunityMention from '@/web/components/community/community-mention';

export default {
  name: 'CommentComponent',
  components: {
    CommunityCompose,
    CommunityProfilePreview,
    CommunityCommentNode,
  },
  props: {
    comment: {
      type: Object,
    },
    user: Object,
    hashtagsEnabled: {
      type: Boolean,
    },
    hashtags: {
      type: Array,
    },
  },
  computed: {
    ...mapGetters({
      studioCommunityProfileId: 'user/studioCommunityProfileId',
      interactAsStudio: 'community/interactAsStudio',
      studio: 'studio/currentStudio',
    }),
    hasReplies() {
      return (
        this.comment && this.comment.replies && this.comment.replies.length > 0
      );
    },
  },
  data() {
    return {
      repliesHidden: false,
    };
  },
  methods: {
    jsonToHtmlContent(jsonContent) {
      if (typeof jsonContent === 'string') {
        jsonContent = JSON.parse(jsonContent);
      }
      try {
        return sanitize(
          generateHTML(jsonContent, [
            Document,
            Paragraph,
            Text,
            CommunityMention.CommunityMention,
            Image,
            HardBreak,
            Heading,
            Code,
            CodeBlock,
            BulletList,
            OrderedList,
            ListItem,
            Link,
            Italic,
            Bold,
            Blockquote,
          ])
        );
      } catch (e) {
        console.log(e);
        return '<p class="c-coral">ERROR parsing comment!</p>';
      }
    },
    async likeComment(comment) {
      const iLiked = comment.liked;
      const iNumLikes = comment.num_likes || 0;

      try {
        // be optimistic for better UX
        this.$set(comment, 'num_likes', comment.num_likes + 1 || 1);
        this.$set(comment, 'liked', true);

        const response = await this.$store.dispatch('community/likeComment', {
          commentId: comment.id,
        });
        this.$set(comment, 'num_likes', response.num_likes || 1);
      } catch (error) {
        // revert
        this.$set(comment, 'num_likes', iNumLikes);
        this.$set(comment, 'liked', iLiked);
      }
    },
    async unlikeComment(comment) {
      const iLiked = comment.liked;
      const iNumLikes = comment.num_likes || 0;
      try {
        this.$set(
          comment,
          'num_likes',
          Math.max(comment.num_likes - 1 || 0, 0)
        );
        this.$set(comment, 'liked', false);
        const response = await this.$store.dispatch('community/unlikeComment', {
          commentId: comment.id,
        });
        this.$set(comment, 'num_likes', response.num_likes);
      } catch (error) {
        this.$set(comment, 'num_likes', iNumLikes);
        this.$set(comment, 'liked', iLiked);
      }
    },
    showLikes(comment) {
      this.$modal.show(CommentLikesModal, {
        commentId: comment.id,
        numLikes: comment.num_likes,
      });
    },
    replyOnComment(comment) {
      this.$set(comment, 'showReply', !comment.showReply);
    },
    addComment(data) {
      this.$emit('addComment', data);
    },
    canModerateComment(comment) {
      const isCommentOwner = this.user.id === comment.user.id;
      const isCommunityAdmin =
        this.studioCommunityProfileId === comment.entity_owner &&
        this.interactAsStudio;
      return isCommentOwner || isCommunityAdmin;
    },
    dateFromNow(date) {
      return moment(date).fromNow();
    },
    async hideComment(comment) {
      try {
        await this.$store.dispatch('community/hideComment', {
          commentId: comment.id,
        });
        comment.hidden = true;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    async showComment(comment) {
      try {
        await this.$store.dispatch('community/showComment', {
          commentId: comment.id,
        });
        comment.hidden = false;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    async deleteComment(comment) {
      try {
        await this.$store.dispatch('community/deleteComment', {
          commentId: comment.id,
        });
        comment.deleted = true;
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    getUserById(id) {
      // TODO implement find user by id
      return {
        id,
        name: null,
      };
    },
    setCommentUserId(comment) {
      return `comment_id-${comment.id}_user_id-${comment.user.id}`;
    },
    async flagComment(comment) {
      try {
        const updatedComment = await this.$store.dispatch(
          'community/flagComment',
          {
            commentId: comment.id,
          }
        );
        comment.flagged = true;
        comment.num_flags = updatedComment.num_flags;
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'COMMUNITY.NOTIFICATIONS.COMMENT_WAS_SUCCESSFULLY_FLAGGED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    async unFlagComment(comment) {
      try {
        const updatedComment = await this.$store.dispatch(
          'community/unFlagComment',
          {
            commentId: comment.id,
          }
        );
        comment.flagged = false;
        comment.num_flags = updatedComment.num_flags;
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'COMMUNITY.NOTIFICATIONS.COMMENT_WAS_SUCCESSFULLY_UNFLAGGED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    async pinComment(comment) {
      try {
        await this.$store.dispatch('community/pinComment', {
          commentId: comment.id,
        });
        comment.is_pinned = true;
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'COMMUNITY.NOTIFICATIONS.COMMENT_WAS_SUCCESSFULLY_PINNED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    async unPinComment(comment) {
      try {
        await this.$store.dispatch('community/unPinComment', {
          commentId: comment.id,
        });
        comment.is_pinned = false;
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t(
            'COMMUNITY.NOTIFICATIONS.COMMENT_WAS_SUCCESSFULLY_UNPINNED'
          ),
        });
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: this.$t('NOTIFICATIONS.TITLE.ERROR'),
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1,
        });
      }
    },
    viewProfile(data) {
      const route = this.$router.resolve({
        name: 'student-community-profile-view',
        params: { id: data.user.id },
      });
      window.open(route.href, '_blank');
    },
    commentOpacity(comment) {
      const minOpacity = 0;
      const maxOpacity = 1;

      if (comment.num_flags > 9) {
        return minOpacity;
      }
      return maxOpacity - comment.num_flags * 0.1;
    },
    getEditText(comment) {
      return comment.hidden
        ? '<i>This comment is hidden</i>'
        : this.jsonToHtmlContent(comment.content);
    },
    openEditModel(comment) {
      this.$modal.show(
        CommunityCommentEditModal,
        {
          confirm: this.onCommentEdit,
          comment,
          user: this.user,
          editText: this.jsonToHtmlContent(comment.content),
          allowScheduling: !!comment.release_on,
          hashtagsEnabled: this.hashtagsEnabled,
          hashtags: this.hashtags,
        },
        {
          classes: ['vm--inline-modal'],
        }
      );
    },
    editComment(comment) {
      this.$emit('editComment', comment);
    },
    onCommentEdit(comment) {
      this.$emit('editComment', comment);
    },
    canEditComment(comment) {
      return !comment.hidden && this.user.id === comment.user.id;
    },
    hasAttachment(comment) {
      return comment?.attachment_type != null;
    },
    getAttachment(comment) {
      return {
        attachment_type: comment.attachment_type,
        attached_title: comment.attached_title,
        attached_description: comment.attached_description,
        attached_image: comment.attached_image,
        attached_url: comment.attached_url,
        attached_favicon_url: comment.attached_favicon_url,
      };
    },
    schedulePost(comment) {
      this.$modal.show(CommunityScheduleModal, {
        releaseOn: comment.releaseOn,
        confirmFn: date => {
          comment.release_on = date;
          this.editComment(comment);
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.comment {
  &__block-wrapper {
    width: 100%;
    overflow: hidden;
    word-break: break-word;

    &--small {
      width: auto;
    }

    &.scheduled {
      opacity: 0.9;
    }
  }

  &__header {
    .popup-menu {
      position: absolute;
      right: 1rem;
      top: 1rem;
    }
  }

  &__block {
    padding: 1rem;
    border: 1px solid $light-gray;
    border-radius: $border-radius;
    background-color: $white;
    position: relative;

    &--opened {
      border-radius: $border-radius $border-radius 0 0;
      border-bottom-color: transparent;
    }

    &--small {
      padding: 9px 16px 6px 16px;
      border-radius: $border-radius;
      border: none;
    }
  }

  &__label {
    font-size: 12px;
    font-weight: 400;
    color: $dark-gray;

    &--liked {
      color: var(--brand-color, $coral);
    }

    &--medium-grey {
      color: $medium-gray;
    }
  }

  &__like-reply-block {
  }

  &__like-block {
    position: absolute;
    right: 5px;
    bottom: -10px;
    background-color: $white;
    border-radius: $border-radius;
    box-shadow: 2px 3px 9px -7px $black;
    &:hover .comment__label {
      text-decoration: underline;
    }
  }

  &__like-container {
    background-color: $white;
    border-radius: $border-radius;
    text-align: center;
    cursor: pointer;
  }

  &__like-icon {
    color: var(--brand-color, $coral);

    &--comments {
      flex: 1;
      font-size: 11px;
      color: var(--brand-color, $coral);
    }
  }

  &__reply-block {
    color: $white;
    background-color: $white;

    &.hidden {
      display: none !important;
    }

    &--nested {
      border-top: none !important;
      border-radius: 0 0 $border-radius $border-radius !important;
    }
  }

  &__icon {
    cursor: pointer;
    height: 48px;
    width: 48px;

    &--small {
      margin-left: 0;
      height: 40px;
      width: 40px;
    }
  }

  &__like-number--active:hover {
    text-decoration: underline;
  }

  &__children {
    background: $lightest-gray;
    margin: 0 !important;
    padding-top: 12px;
  }

  &__children:last-of-type {
    border-radius: 0 0 $border-radius $border-radius;
  }

  &__circle {
    color: $medium-gray;
    font-size: 4px;
  }

  p {
    margin: 0;
  }
}

::v-deep {
  .mention {
    color: var(--brand-color, $coral);
    padding-right: 5px;
  }
}

.reply-comments {
  border-radius: $border-radius;
}

.comment-content {
  color: $charcoal;

  ::v-deep {
    p {
      margin-bottom: 0.5rem;
    }
    img {
      border-radius: 4px;
      max-width: 50vw;
      width: 300px;
      margin-bottom: 0.5rem;
      margin-right: 0.5rem;
    }
    a {
      color: var(--brand-color, $coral);
    }
  }
}

.author-name {
  font-weight: 600;
}

.author-role {
  color: $dark-gray;
  &.student {
    display: none !important;
  }
}

.charcoal-on-hover {
  &:hover {
    color: $charcoal;
    transition: all 0.2s ease-in-out;
  }
}

.underline-on-hover {
  &:hover {
    text-decoration: underline;
    transition: all 0.2s ease-in-out;
  }
}

hr {
  border-top-color: $lightest-gray;
}

ul {
  padding-left: 0;
  margin-left: 0;
  margin-bottom: 10px;
}

ul li {
  list-style-type: none;
}

.hashtag {
  border: 1px solid var(--brand-color, $coral);
  border-radius: $border-radius;
  background-color: transparent;
  color: var(--brand-color, $coral);
  padding: 4px 8px;
  outline: none;

  &:hover {
    background-color: #f7f7f7;
  }
}

.calendar-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: var(--brand-color-with-opacity-03, $coral);
  color: var(--brand-color, $coral);
}
</style>
